import React, { useEffect, useState } from 'react';
import { Plus, ClipboardList, CheckCircle2, Clock, XCircle, Play, Truck, Filter } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const TASK_TYPES = [
  { value: 'mobilization', label: 'Mobilization' },
  { value: 'demobilization', label: 'Demobilization' },
  { value: 'replace_equipment', label: 'Replace Equipment' },
  { value: 'replace_driver', label: 'Replace Driver' },
  { value: 'invoice_submission', label: 'Invoice Submission' },
  { value: 'timesheet_collection', label: 'Timesheet Collection' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_COLORS = {
  low: 'bg-slate-100 text-slate-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

const STATUS_ICONS = {
  pending: Clock,
  in_progress: Play,
  completed: CheckCircle2,
  cancelled: XCircle,
};

const STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const emptyForm = {
  task_type: 'mobilization',
  title: '',
  description: '',
  company_id: '',
  assignee_id: '',
  linked_dn_id: '',
  linked_po_id: '',
  priority: 'normal',
  due_date: '',
  notes: '',
};

export const JobAssignmentsPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const canAssign = ['admin', 'operation_manager', 'operations'].includes(role);

  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const [t, c] = await Promise.all([
        api.get('/job-assignments', { params }),
        canAssign ? api.get('/companies') : Promise.resolve({ data: [] }),
      ]);
      setTasks(t.data || []);
      setCompanies(c.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadAssignees = async (companyId) => {
    if (!companyId) { setAssignees([]); return; }
    try {
      const r = await api.get('/job-assignments/eligible-assignees', { params: { company_id: companyId } });
      setAssignees(r.data || []);
    } catch (e) {
      setAssignees([]);
    }
  };

  const openCreate = () => {
    setForm({ ...emptyForm });
    setAssignees([]);
    setCreateOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_id || !form.assignee_id || !form.title.trim()) {
      toast.error('Company, Assignee and Title are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        task_type: form.task_type,
        title: form.title,
        description: form.description || null,
        company_id: form.company_id,
        assignee_id: form.assignee_id,
        linked_dn_id: form.linked_dn_id || null,
        linked_po_id: form.linked_po_id || null,
        priority: form.priority,
        due_date: form.due_date || null,
        notes: form.notes || null,
      };
      await api.post('/job-assignments', payload);
      toast.success('Task assigned');
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (task, newStatus) => {
    try {
      await api.patch(`/job-assignments/${task.id}/status`, { status: newStatus });
      toast.success('Status updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="space-y-6" data-testid="job-assignments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
            <ClipboardList className="h-8 w-8" /> Job Assignments
          </h1>
          <p className="text-muted-foreground mt-1">
            {canAssign
              ? 'Assign operational tasks to your executives and coordinators.'
              : 'Tasks assigned to you.'}
          </p>
        </div>
        {canAssign && (
          <Button onClick={openCreate} data-testid="new-task-button">
            <Plus className="h-4 w-4 mr-2" /> Assign New Task
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56" data-testid="task-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading...</div>
          ) : tasks.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <ClipboardList className="mx-auto h-12 w-12 mb-2 opacity-40" />
              No tasks yet.
            </div>
          ) : (
            <div className="divide-y">
              {tasks.map((t) => {
                const StatusIcon = STATUS_ICONS[t.status] || Clock;
                const isMyTask = t.assignee_id === user?.id;
                return (
                  <div key={t.id} className="p-4 flex flex-col sm:flex-row gap-3 justify-between hover:bg-slate-50" data-testid={`task-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusIcon className="h-4 w-4 shrink-0" />
                        <span className="font-semibold">{t.title}</span>
                        <Badge className={STATUS_COLORS[t.status]}>{t.status.replace('_', ' ')}</Badge>
                        <Badge className={PRIORITY_COLORS[t.priority]}>{t.priority}</Badge>
                        <Badge variant="outline">{(TASK_TYPES.find(x => x.value === t.task_type) || {}).label || t.task_type}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        <b>{isMyTask ? 'From' : 'To'}:</b>{' '}
                        {isMyTask ? t.assigner_name : t.assignee_name}
                        {t.due_date && <> · Due {t.due_date}</>}
                      </p>
                      {t.description && <p className="text-sm mt-1 truncate">{t.description}</p>}
                      {(t.linked_dn_id || t.linked_po_id) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Linked:{' '}
                          {t.linked_dn_id && <span className="font-mono">DN {t.linked_dn_id.slice(-6)}</span>}
                          {t.linked_dn_id && t.linked_po_id && ' · '}
                          {t.linked_po_id && <span className="font-mono">PO {t.linked_po_id.slice(-6)}</span>}
                        </p>
                      )}
                    </div>
                    {isMyTask && t.status !== 'completed' && t.status !== 'cancelled' && (
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {t.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(t, 'in_progress')} data-testid={`start-task-${t.id}`}>
                            <Play className="h-3.5 w-3.5 mr-1" /> Start
                          </Button>
                        )}
                        <Button size="sm" onClick={() => updateStatus(t, 'completed')} data-testid={`complete-task-${t.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Complete
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="new-task-dialog">
          <DialogHeader>
            <DialogTitle>Assign Task</DialogTitle>
            <DialogDescription>Link to a specific Delivery Note or PO to track cost/profit against a sale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Task Type *</Label>
              <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
            </div>
            <div>
              <Label>Company *</Label>
              <Select value={form.company_id} onValueChange={(v) => { setForm({ ...form, company_id: v, assignee_id: '' }); loadAssignees(v); }}>
                <SelectTrigger data-testid="task-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assignee *</Label>
              <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })} disabled={!form.company_id}>
                <SelectTrigger data-testid="task-assignee-select">
                  <SelectValue placeholder={form.company_id ? 'Select executive/coordinator' : 'Pick a company first'} />
                </SelectTrigger>
                <SelectContent>
                  {assignees.length === 0 && (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      {form.company_id ? 'No eligible executives/coordinators in this company' : 'Select a company first'}
                    </div>
                  )}
                  {assignees.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name || u.email} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" />
              </div>
            </div>
            <div>
              <Label>Linked Delivery Note ID (optional)</Label>
              <Input value={form.linked_dn_id} onChange={(e) => setForm({ ...form, linked_dn_id: e.target.value })} placeholder="dn_..." />
            </div>
            <div>
              <Label>Linked Client PO ID (optional)</Label>
              <Input value={form.linked_po_id} onChange={(e) => setForm({ ...form, linked_po_id: e.target.value })} placeholder="cpo_..." />
            </div>
            <div>
              <Label>Description / Notes</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="task-submit-button">
              {saving ? 'Assigning...' : 'Assign Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobAssignmentsPage;
